/** Full search results page layout with filters, vendor tabs, and pagination. */

import { h, Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { ResultCard, SkeletonCard } from './ResultCard';
import { FamilyStack } from './FamilyStack';
import { FilterPanel } from './FilterPanel';
import { Pagination } from './Pagination';
import { NoResults } from './NoResults';
import type { SearchResponse } from '../api';
import type { useFilters } from '../hooks/useFilters';
import { getConfig } from '../config';

export type VendorTab = 'terra' | 'le';
export interface VendorCounts {
  terra: number | null;
  le: number | null;
}

function formatPriceFilter(value: string): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  return `$${parsed.toLocaleString()}`;
}

interface ResultsPageProps {
  query: string;
  results: SearchResponse | null;
  isLoading: boolean;
  error: string | null;
  page: number;
  sort: string;
  vendorTab: VendorTab;
  vendorCounts: VendorCounts;
  filterHook: ReturnType<typeof useFilters>;
  onPageChange: (page: number) => void;
  onSortChange: (sort: string) => void;
  onSearch: (query: string) => void;
  onVendorTabChange: (tab: VendorTab) => void;
}

export function ResultsPage({
  query,
  results,
  isLoading,
  error,
  page,
  sort,
  vendorTab,
  vendorCounts,
  filterHook,
  onPageChange,
  onSortChange,
  onSearch,
  onVendorTabChange,
}: ResultsPageProps) {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { perPage } = getConfig();
  const {
    filters,
    updateFilters,
    clearFilters,
    toggleCategory,
    toggleCertification,
    toggleManufacturer,
    hasActiveFilters,
  } = filterHook;

  const mobileFilterToggleRef = useRef<HTMLButtonElement | null>(null);
  const mobileFilterPanelRef = useRef<HTMLDivElement | null>(null);

  // Focus trap for the mobile filter overlay — keyboard users can't tab out of the modal.
  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const panel = mobileFilterPanelRef.current;
    if (!panel) return;

    const focusableSel = 'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const getFocusables = () => Array.from(panel.querySelectorAll<HTMLElement>(focusableSel));

    // Focus first focusable on open
    const first = getFocusables()[0];
    first?.focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMobileFiltersOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = getFocusables();
      if (focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      const active = panel.getRootNode() instanceof ShadowRoot
        ? (panel.getRootNode() as ShadowRoot).activeElement
        : document.activeElement;
      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      mobileFilterToggleRef.current?.focus();
    };
  }, [mobileFiltersOpen]);

  // Lock page scroll behind the filter drawer — otherwise the storefront
  // body keeps scrolling under the fixed overlay on touch devices.
  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileFiltersOpen]);

  // Close the drawer if the viewport grows into desktop sidebar mode
  // (≥1024px, e.g. tablet rotation) — otherwise the sidebar and the
  // scroll-locked drawer are both up at once.
  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const mq = window.matchMedia('(min-width: 1024px)');
    if (mq.matches) {
      setMobileFiltersOpen(false);
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileFiltersOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mobileFiltersOpen]);

  const resultsTotal = results?.total ?? 0;
  const displayQuery = query.trim();
  const facets = results?.facets ?? {};
  const products = results?.results ?? [];

  const categoryFacets = facets.category?.buckets ?? [];
  const certFacets = facets.certifications_list?.buckets ?? [];
  const manufacturerFacets = facets.manufacturer?.buckets ?? [];
  const didYouMean = results?.did_you_mean;
  const relaxedQueries = results?.relaxed_queries ?? [];
  const searchEventId = (results as (SearchResponse & { search_event_id?: string }) | null)?.search_event_id;
  const activeVendorReportedTotal = vendorTab === 'terra'
    ? (vendorCounts.terra ?? resultsTotal)
    : (vendorCounts.le ?? resultsTotal);
  const lastPageVisibleCount = products.length > 0 && products.length < perPage
    ? ((page - 1) * perPage) + products.length
    : null;
  const total = lastPageVisibleCount != null
    ? Math.min(activeVendorReportedTotal, lastPageVisibleCount)
    : activeVendorReportedTotal;
  const terraCount = vendorTab === 'terra'
    ? total
    : vendorCounts.terra;
  const leCount = vendorTab === 'le'
    ? total
    : vendorCounts.le;
  const activeFilterChips: { key: string; label: string; remove: () => void }[] = [];
  if (filters.inStockOnly) {
    activeFilterChips.push({ key: 'in-stock', label: 'In Stock only', remove: () => updateFilters({ inStockOnly: false }) });
  }
  if (filters.priceMin || filters.priceMax) {
    const label = filters.priceMin && filters.priceMax
      ? `${formatPriceFilter(filters.priceMin)} - ${formatPriceFilter(filters.priceMax)}`
      : filters.priceMin
        ? `${formatPriceFilter(filters.priceMin)}+`
        : `Up to ${formatPriceFilter(filters.priceMax)}`;
    activeFilterChips.push({
      key: 'price',
      label,
      remove: () => updateFilters({ priceMin: '', priceMax: '' }),
    });
  }
  filters.categories.forEach((c) => {
    activeFilterChips.push({ key: `cat:${c}`, label: c, remove: () => toggleCategory(c) });
  });
  filters.certifications.forEach((c) => {
    const isoMatch = c.match(/^ISO\s*(\d+(?:\.\d+)?)$/i);
    activeFilterChips.push({
      key: `cert:${c}`,
      label: isoMatch ? `ISO Grade: ${isoMatch[1]}` : c,
      remove: () => toggleCertification(c),
    });
  });
  filters.manufacturers.forEach((m) => {
    activeFilterChips.push({ key: `manufacturer:${m}`, label: m, remove: () => toggleManufacturer(m) });
  });
  return (
    <div class={`ts-results-page ${vendorTab === 'le' ? 'ts-results-page--le' : ''}`}>
      {/* Header */}
      <div class="ts-results-header">
        <div class="ts-cockpit-top">
          <div class="ts-results-header-left">
            {!isLoading && results && (
              <div class="ts-results-context">
                <h2 class="ts-results-count">
                  <span class="ts-results-count-number">{total.toLocaleString()}</span>
                  <span> result{total !== 1 ? 's' : ''}</span>
                  {displayQuery && (
                    <Fragment>
                      <span> for </span>
                      <span class="ts-results-count-query">&ldquo;{displayQuery}&rdquo;</span>
                    </Fragment>
                  )}
                </h2>

                <div class="ts-vendor-switch" role="group" aria-label="Catalog">
                  <button
                    class={`ts-vendor-segment ts-vendor-segment--terra ${vendorTab === 'terra' ? 'ts-vendor-segment--active' : ''}`}
                    onClick={() => onVendorTabChange('terra')}
                    aria-pressed={vendorTab === 'terra'}
                    aria-current={vendorTab === 'terra' ? 'page' : undefined}
                    aria-label={`Terra Universal, ${terraCount != null ? terraCount.toLocaleString() : 'unknown'} results`}
                  >
                    <span class="ts-vendor-segment-title">Terra Universal</span>
                    {terraCount != null && <span class="ts-vendor-segment-count">{terraCount.toLocaleString()}</span>}
                  </button>
                  <button
                    class={`ts-vendor-segment ts-vendor-segment--le ${vendorTab === 'le' ? 'ts-vendor-segment--active' : ''}`}
                    onClick={() => onVendorTabChange('le')}
                    aria-pressed={vendorTab === 'le'}
                    aria-current={vendorTab === 'le' ? 'page' : undefined}
                    aria-label={`Laboratory Equipment, ${leCount != null ? leCount.toLocaleString() : 'unknown'} results`}
                  >
                    <span class="ts-vendor-segment-title">Laboratory Equipment</span>
                    {leCount != null && <span class="ts-vendor-segment-count">{leCount.toLocaleString()}</span>}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div class="ts-results-header-right">
            <button
              class="ts-filter-toggle"
              onClick={() => setMobileFiltersOpen(true)}
              ref={mobileFilterToggleRef}
              aria-haspopup="dialog"
              aria-expanded={mobileFiltersOpen}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
              </svg>
              Filters
              {activeFilterChips.length > 0 && (
                <span class="ts-filter-badge">{activeFilterChips.length}</span>
              )}
            </button>
            <div class="ts-sort-wrap">
              <span class="ts-sort-label" aria-hidden="true">Sort by</span>
              <select class="ts-sort-select" value={sort} onChange={(e) => onSortChange((e.target as HTMLSelectElement).value)} aria-label="Sort results">
                <option value="relevance">Relevance</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="name_asc">Name: A to Z</option>
              </select>
            </div>
          </div>
        </div>

        {activeFilterChips.length > 0 && (
          <div class="ts-cockpit-filter-row">
            <div class="ts-filter-chips" aria-label="Active filters">
              {activeFilterChips.map((c) => (
                <button
                  key={c.key}
                  class="ts-filter-chip"
                  onClick={c.remove}
                  aria-label={`Remove filter ${c.label}`}
                >
                  <span class="ts-filter-chip-label">{c.label}</span>
                  <span class="ts-filter-chip-close" aria-hidden="true">x</span>
                </button>
              ))}
              <button class="ts-filter-chip-clear" onClick={clearFilters}>Clear all</button>
            </div>
          </div>
        )}
      </div>

      {error && <div class="ts-error"><p>Something went wrong. Please try again.</p></div>}

      <div class="ts-results-layout">
        <aside class="ts-filter-sidebar">
          <FilterPanel
            facets={{ categories: categoryFacets, certifications: certFacets, manufacturers: manufacturerFacets }}
            filters={filters}
            onToggleCategory={toggleCategory}
            onToggleCertification={toggleCertification}
            onToggleManufacturer={toggleManufacturer}
            onFilterChange={updateFilters}
          />
        </aside>

        {mobileFiltersOpen && (
          <div class="ts-filter-overlay" role="dialog" aria-modal="true" aria-label="Filters">
            <div class="ts-filter-overlay-backdrop" onClick={() => setMobileFiltersOpen(false)} />
            <div class="ts-filter-overlay-panel" ref={mobileFilterPanelRef}>
              <div class="ts-filter-overlay-header">
                <h3>Filters</h3>
                <button onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>
              <div class="ts-filter-overlay-body">
                <FilterPanel
                  facets={{ categories: categoryFacets, certifications: certFacets, manufacturers: manufacturerFacets }}
                  filters={filters}
                  onToggleCategory={toggleCategory}
                  onToggleCertification={toggleCertification}
                  onToggleManufacturer={toggleManufacturer}
                  onFilterChange={updateFilters}
                />
              </div>
              <div class="ts-filter-overlay-footer">
                <button type="button" class="ts-filter-overlay-apply" onClick={() => setMobileFiltersOpen(false)}>
                  {results && !isLoading
                    ? `Show ${total.toLocaleString()} result${total === 1 ? '' : 's'}`
                    : 'Show results'}
                </button>
                {hasActiveFilters && (
                  <button type="button" class="ts-filter-overlay-clear" onClick={clearFilters}>
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div class="ts-results-main" id="ts-results-main" tabindex={-1}>
          {isLoading ? (
            <div class="ts-results-list">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} viewMode="list" />)}
            </div>
          ) : products.length === 0 && results && page > 1 ? (
            <div class="ts-empty-page">
              <h3 class="ts-empty-page-title">That page ran out of results</h3>
              <p class="ts-empty-page-copy">Go back one page or tighten the query and filters.</p>
              <button class="ts-link" onClick={() => onPageChange(page - 1)}>
                Return to page {page - 1}
              </button>
            </div>
          ) : products.length === 0 && results ? (
            <NoResults query={query} suggestion={null} didYouMean={didYouMean} relaxedQueries={relaxedQueries} onSearch={onSearch} />
          ) : (
            <div class="ts-results-list">
              {products.map((product, index) => {
                // Render FamilyStack with the inline variant table
                // on:
                //   1. ANY child product that has a parent_product_id —
                //      Reading A from stakeholder review: every child card surfaces
                //      a path to its parent landing page + sibling
                //      a family model selector, not just children with 2+ siblings.
                //   2. PARENT products that have 1+ children — the parent
                //      landing-page card itself.
                // Wrap any product that has family context. We don't gate on
                // sibling_count > 0 here because some parent products in the
                // index have sibling_count missing/null even though they DO
                // have children — FamilyStack will discover the real count
                // via /family/{id} on mount and self-hide if there's truly nothing.
                const hasFamilyContext =
                  (product.parent_product_id != null)
                  || product.is_parent_product;
                // Server now dedupes families (one representative per family),
                // so each product is unique. Use FamilyStack when siblings exist
                // so the user can expand to see variants.
                return hasFamilyContext ? (
                  <FamilyStack
                    key={product.product_id}
                    product={product}
                    query={query}
                    position={index}
                    searchEventId={searchEventId}
                  />
                ) : (
                  <ResultCard
                    key={product.product_id}
                    product={product}
                    query={query}
                    position={index}
                    viewMode="list"
                  />
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && results && total > 0 && (
            <div class="ts-pagination-wrapper">
              <Pagination
                page={page}
                totalResults={total}
                perPage={perPage}
                pageResultCount={products.length}
                onPageChange={onPageChange}
              />
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
